import { useRef, useState } from "react";
import client from "@/lib/api";
import AuthedImage from "@/components/AuthedImage";
import { X, Plus } from "lucide-react";

// Overlays draggable black-out boxes on an (unredacted) source plan page so
// staff can confirm/adjust the AI-suggested redaction before publishing.
// boxes: [{id,x0,y0,x1,y1,label,source}] as 0-1 fractions. onChange(newBoxes).
export default function RedactionEditor({ imgSrc, boxes, onChange }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState(null); // {x0,y0,x1,y1} while dragging

  const toFrac = (e) => {
    const r = ref.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  const onDown = (e) => {
    if (e.target.dataset.role === "remove") return;
    e.preventDefault();
    const p = toFrac(e);
    ref.current.setPointerCapture?.(e.pointerId);
    setDraft({ sx: p.x, sy: p.y, x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };
  const onMove = (e) => {
    if (!draft) return;
    const p = toFrac(e);
    setDraft((d) => ({
      ...d,
      x0: Math.min(d.sx, p.x), y0: Math.min(d.sy, p.y),
      x1: Math.max(d.sx, p.x), y1: Math.max(d.sy, p.y),
    }));
  };
  const onUp = () => {
    if (!draft) return;
    if (draft.x1 - draft.x0 > 0.01 && draft.y1 - draft.y0 > 0.01) {
      onChange([...(boxes || []), {
        id: "box_" + Math.random().toString(36).slice(2, 10),
        x0: +draft.x0.toFixed(4), y0: +draft.y0.toFixed(4), x1: +draft.x1.toFixed(4), y1: +draft.y1.toFixed(4),
        label: "manual", source: "manual",
      }]);
    }
    setDraft(null);
  };
  const remove = (id) => onChange((boxes || []).filter((b) => b.id !== id));

  const pct = (v) => `${v * 100}%`;

  return (
    <div className="space-y-2">
      <div
        ref={ref}
        data-testid="redaction-canvas"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        className="relative w-full cursor-crosshair select-none overflow-hidden rounded-lg border border-slate-200 bg-slate-50 touch-none"
      >
        <AuthedImage src={imgSrc} className="block w-full" />
        {(boxes || []).map((b) => (
          <div key={b.id} style={{ left: pct(b.x0), top: pct(b.y0), width: pct(b.x1 - b.x0), height: pct(b.y1 - b.y0) }}
            data-testid={`redaction-box-${b.id}`}
            className="absolute border-2 border-red-500 bg-black/75">
            <button data-role="remove" data-testid={`remove-box-${b.id}`} onPointerDown={(e) => e.stopPropagation()} onClick={() => remove(b.id)}
              className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-red-500 text-white shadow hover:bg-red-600">
              <X className="h-3 w-3" data-role="remove" />
            </button>
          </div>
        ))}
        {draft && (
          <div style={{ left: pct(draft.x0), top: pct(draft.y0), width: pct(draft.x1 - draft.x0), height: pct(draft.y1 - draft.y0) }}
            className="absolute border-2 border-red-400 bg-black/50" />
        )}
      </div>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Plus className="h-3 w-3" /> Drag on the plan to add a black-out box · tap the red ✕ to remove one
      </p>
    </div>
  );
}

export async function fetchRedactionPrepared(client, id) {
  return (await client.post(`/takeoffs/${id}/analyze`)).data;
}
